import { useEffect, useState, useCallback } from 'react';

export interface NavSection {
  id: string;
  label: string;
}

export interface UseSectionNavOptions {
  /** 导航菜单项列表 */
  sections: readonly NavSection[];
  /** 默认激活的 section id */
  defaultActiveId?: string;
  /** header 元素的 id，用于计算偏移高度 */
  headerElId?: string;
  /** footer 元素的 id，用于计算 IntersectionObserver 的 rootMargin */
  footerElId?: string;
}

export interface UseSectionNavReturn {
  /** 当前激活的 section id */
  activeNavId: string;
  /** 滚动到指定 section */
  scrollToId: (id: string) => void;
}

/**
 * 用于处理页面 section 导航的 hook
 * - 监听各 section 可见性，自动更新激活状态
 * - 提供滚动到指定 section 的方法
 */
export function useSectionNav({
  sections,
  defaultActiveId,
  headerElId = 'nav-header',
  footerElId = 'footer-bar',
}: UseSectionNavOptions): UseSectionNavReturn {
  const [activeNavId, setActiveNavId] = useState<string>(defaultActiveId ?? sections[0]?.id ?? '');

  const scrollToId = useCallback(
    (id: string) => {
      const element = document.getElementById(id);
      const headerNode = document.getElementById(headerElId);
      if (element && headerNode) {
        const headerHeight = headerNode.offsetHeight;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight;

        window.scrollTo({
          top: elementPosition,
        });

        setActiveNavId(id);
      }
    },
    [headerElId],
  );

  // 监听 section 可见性，更新 active 导航项
  useEffect(() => {
    const sectionIds = sections.map((s) => s.id);
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    const updateActiveSection = () => {
      if (visibleSections.size > 0) {
        // 按照 sections 的顺序，找到第一个可见的
        for (const sectionId of sectionIds) {
          if (visibleSections.has(sectionId)) {
            setActiveNavId(sectionId);
            break;
          }
        }
      }
    };

    // 获取 header 和 footer 的高度用于计算 rootMargin
    const headerNode = document.getElementById(headerElId);
    const footerNode = document.getElementById(footerElId);
    const headerHeight = headerNode?.clientHeight ?? 0;
    const footerHeight = footerNode?.clientHeight ?? 0;

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleSections.set(id, entry.intersectionRatio);
            } else {
              visibleSections.delete(id);
            }
            updateActiveSection();
          });
        },
        {
          rootMargin: `-${headerHeight}px 0px -${footerHeight}px 0px`,
          threshold: 0,
        },
      );

      observer.observe(element);
      observers.push(observer);
    });

    // 监听滚动事件，处理页面底部的情况
    const handleScroll = () => {
      updateActiveSection();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sections, headerElId, footerElId]);

  return {
    activeNavId,
    scrollToId,
  };
}
